import { isOutsideRange, isAbove, isBelow, isEmail, isValidEmailDomain, isNaN, isValidPasswordFormat } from 'utils/validations/functions';
import { hasValue, isEmptyString } from 'utils/functions';
import { createError } from 'utils/validations/createError';
import moment from 'moment';

/**
 * Returns a function that will validate input.
 * @param {*} min number of selections to verify
 * @param {*} max number of selections to verify
 */
export function createSelectionValidationFn(min, max) {
  const validateFn = function selectionValidation(numChecked) {
    const hasMax = hasValue(max);
    const hasMin = hasValue(min);

    if (hasMax && hasMin && isOutsideRange(numChecked, min, max)) {
      if (min === max) {
        return createError('validation.checkbox.selectionRange', { number: min });
      }
      return createError('validation.checkbox.selectionRangeFromTo', { from: min, to: max });
    }

    if (hasMax && isAbove(numChecked, max)) {
      return createError('validation.checkbox.selectionRangeMax', { max });
    }

    if (hasMin && isBelow(numChecked, min)) {
      return createError('validation.checkbox.selectionRangeMin', { min });
    }

    return null;
  };
  return validateFn;
}

/**
 * Returns a function that will validate input.
 * @param {*} validDomains array of whitelisted domains
 * @param {*} allowEmpty Validation will return NULL if empty strings are allowed
 */
export function createEmailValidationFn(validDomains, allowEmpty) {
  const validateFn = function emailValidation(email) {
    if (allowEmpty && isEmptyString(email)) {
      return null;
    }

    if (!isEmail(email)) {
      return createError('validation.email.badFormat');
    }

    if (validDomains && !isValidEmailDomain(email, validDomains)) {
      return createError('validation.email.badDomain');
    }

    return null;
  };
  return validateFn;
}

/**
 * Returns a function that will validate input.
 * @param {*} minValue Validation will fail if value is below minValue
 * @param {*} maxValue Validation will fail if value is above maxValue
 * @param {*} allowEmpty Validation will return NULL if empty strings are allowed
 */
export function createNumberValidationFn(minValue, maxValue, allowEmpty) {
  const validationFn = function numberValidation(value) {
    if (allowEmpty && isEmptyString(value)) {
      return null;
    }

    if (isNaN(value)) {
      return createError('validation.number.nan');
    }

    const hasMax = hasValue(maxValue);
    const hasMin = hasValue(minValue);

    if (hasMax && hasMin && isOutsideRange(value, minValue, maxValue)) {
      return createError('validation.number.numericRangeError', { from: minValue, to: maxValue });
    }

    if (hasMax && isAbove(value, maxValue)) {
      return createError('validation.number.numericRangeMax', { maxValue });
    }

    if (hasMin && isBelow(value, minValue)) {
      return createError('validation.number.numericRangeMin', { minValue });
    }

    return null;
  };
  return validationFn;
}
/**
 *
 * Returns a function that will validate Date
 * @param {*} minDate Validation will fail if value is below minDate
 * @param {*} maxDate Validation will fail if value is above maxDate
 */

export function createDateInputValidationFn(minDate, maxDate) {
  const validateFn = function dateValidation(inputDate) {
    const utcMinDate = moment.utc(minDate);
    const utcMaxDate = moment.utc(maxDate);
    const utcInputDate = moment.utc(inputDate);
    const isBefore = utcInputDate.isBefore(utcMinDate);
    const isAfter = utcInputDate.isAfter(utcMaxDate);
    if (isBefore || isAfter) {
      return createError('validation.date.badFormatDate');
    }
    return null;
  };
  return validateFn;
}

/**
 * Returns a function that will validate password.
 */
export function createPasswordValidationFn() {
  const validateFn = function passwordValidation(password) {
    if (!isValidPasswordFormat(password)) {
      return createError('validation.password.badFormat');
    }

    return null;
  };
  return validateFn;
}

/**
 * Returns a function that will validate input.
 * @param {*} allowEmpty Validation will return NULL if empty strings are allowed
 */
export function createTextValidationFn(allowEmpty) {
  const validateFn = function textValidation(text) {
    if (allowEmpty && isEmptyString(text)) {
      return null;
    }

    if (isEmptyString(text)) {
      return true;
    }

    return null;
  };
  return validateFn;
}

/**
 * Returns a function that will validate textarea input.
 * @param {*} maxCharactersAllowed Validation will fail if length of textarea input exceeds maxCharactersAllowed.
 */
export function createTextareaValidationFn(maxCharactersAllowed) {
  const validateFn = function textValidation(text) {
    if (text.length > maxCharactersAllowed) {
      return createError('validation.textarea.exceedsCharacterLimit', { characterLimit: maxCharactersAllowed });
    }
    return null;
  };
  return validateFn;
}

export default {
  createSelectionValidationFn,
  createEmailValidationFn,
  createNumberValidationFn,
  createPasswordValidationFn,
  createTextValidationFn,
  createDateInputValidationFn,
  createTextareaValidationFn
};
